// One sidebar row that is a page: its icon tile and label, or — inside a
// dropdown (`nested`) — the label alone, indented under its parent's label.
// The page you are on carries aria-current="page" and the tint and bar.
import React from 'react';
import { Box, ListItemButton, Skeleton, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { IconTile, RowLabel, navRowSx } from './navRow';

const countLabel = (count) => (count > 99 ? '99+' : String(count));

export default function SidebarNavItem({ item, active, expanded, nested = false, onNavigate }) {
  const theme = useTheme();
  const { icon, label, disabled = false, loading = false } = item;
  const count = Number(item.badge) || 0;

  // An item with an href (Help & contact) is a real link that opens a new tab.
  const action = item.href
    ? { component: 'a', href: item.href, target: '_blank', rel: 'noopener noreferrer' }
    : { onClick: () => onNavigate(item) };

  const row = (
    <ListItemButton
      {...action}
      disabled={disabled}
      selected={active}
      aria-current={active ? 'page' : undefined}
      aria-label={expanded ? undefined : label}
      sx={navRowSx(theme, { expanded, nested })}
    >
      {!nested && <IconTile icon={icon} highlighted={active} dot={!expanded && count > 0} />}
      {expanded && <RowLabel>{loading ? <Skeleton variant="text" width={80} /> : label}</RowLabel>}
      {expanded && count > 0 && (
        <Box
          component="span"
          aria-label={`${count} unresolved`}
          sx={{
            minWidth: 22, height: 20, px: 0.75, borderRadius: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1), border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          {countLabel(count)}
        </Box>
      )}
    </ListItemButton>
  );

  if (expanded) return row;
  return (
    <Tooltip title={count ? `${label} · ${countLabel(count)}` : label} placement="right">
      <span>{row}</span>
    </Tooltip>
  );
}
